import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW_CARD,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';
import textToReactNodes from 'utils/textToReactNodes';

import type { FilledMessage, Template } from './types';

interface Props {
  /** The selected template — used to render header media (image/video/document). */
  template: Template | null;
  /** The template rendered with the entered variable values. */
  message: FilledMessage;
}

/**
 * The "Mensaje a enviar" preview: header text + header media, body, footer and buttons, exactly
 * as WhatsApp will render the template. Shared by the send-template and create-lead modals.
 */
const TemplatePreview: FC<Props> = ({ template, message }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: RADIUS_CARD,
      bgcolor: SURFACE_CONTAINER_LOW,
      boxShadow: SHADOW_CARD,
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    }}
  >
    {message.HEADER && (
      <Typography variant="h6" sx={{ whiteSpace: 'pre-line', mb: 1.5 }}>
        {message.HEADER}
      </Typography>
    )}
    {template?.components.map((component) => {
      if (component.type === 'HEADER' && component.format === 'IMAGE' && component.example?.header_handle) {
        return (
          <Box key={component.type} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <img
              src={component.example.header_handle[0]}
              alt="Header"
              style={{ width: '50%', height: 'auto', margin: 'auto' }}
            />
          </Box>
        );
      }
      if (component.type === 'HEADER' && component.format === 'VIDEO' && component.example?.header_handle) {
        return (
          <Box key={component.type} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <video
              src={component.example.header_handle[0]}
              autoPlay
              muted
              loop
              style={{ width: '50%', height: 'auto', margin: 'auto' }}
            />
          </Box>
        );
      }
      if (component.type === 'HEADER' && component.format === 'DOCUMENT' && component.example?.header_handle) {
        const fileUrl = component.example.header_handle[0];
        const fileName = fileUrl.split('?')[0].split('/').pop();
        const displayName = fileName?.replace(/^[a-z0-9]*-/, ''); // Remove the initial random string from the filename
        return (
          <Box key={component.type} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                borderRadius: RADIUS_CARD,
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: alpha(NAVY, 0.03),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <PictureAsPdfIcon color="error" />
                <Typography variant="body1" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayName}
                </Typography>
              </Box>
              <IconButton
                component="a"
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="download"
                sx={{ color: alpha(NAVY, 0.55), '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) } }}
              >
                <DownloadIcon />
              </IconButton>
            </Paper>
          </Box>
        );
      }
      return null;
    })}
    {message.BODY && (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 1.5 }}>
        {textToReactNodes(message.BODY)}
      </Typography>
    )}
    {message.FOOTER && (
      <Typography variant="body2" sx={{ color: alpha(NAVY, 0.55), whiteSpace: 'pre-line', mb: 1.5 }}>
        {message.FOOTER}
      </Typography>
    )}
    {message.BUTTONS.some((b) => b.text !== '') && (
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: NAVY }}>
          Botones:
        </Typography>
        {message.BUTTONS.map(
          (button) =>
            button.text && (
              <Button
                key={button.text}
                variant="text"
                fullWidth
                sx={{
                  justifyContent: 'center',
                  textTransform: 'none',
                  borderRadius: RADIUS_COIN,
                  py: 1.25,
                  px: 2,
                  backgroundColor: SURFACE_CONTAINER_LOWEST,
                  color: NAVY,
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.16)}`,
                  transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
                  '&:hover': {
                    backgroundColor: SURFACE_CONTAINER_LOWEST,
                    boxShadow: `inset 0 0 0 1.5px ${NAVY}`,
                  },
                }}
              >
                {button.text}
                {button.url && (
                  <Typography component="span" sx={{ ml: 1, color: alpha(NAVY, 0.55), fontSize: '0.875rem' }}>
                    ({button.url})
                  </Typography>
                )}
              </Button>
            )
        )}
      </Stack>
    )}
  </Paper>
);

export default TemplatePreview;
